import { useState, useEffect } from 'react';
import { X, Plus, Link as LinkIcon, FileText, AlertTriangle, Euro, Edit } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { LinkInvoiceModal } from './LinkInvoiceModal';
import { CreateInvoiceModal } from './CreateInvoiceModal';
import { EditFundCallModal } from './EditFundCallModal';

interface FundCall {
  id: string;
  name: string;
  call_date: string;
  requested_amount: number;
  received_amount: number;
  usage_description: string;
  notes: string;
}

interface LinkedInvoice {
  id: string;
  invoice_id: string;
  allocated_amount: number;
  justification_note: string;
  invoice: {
    supplier: string;
    invoice_date: string;
    total_amount: number;
    invoice_number: string;
  };
}

interface FundCallDetailModalProps {
  fundCall: FundCall;
  subsidyName: string;
  onClose: () => void;
  onUpdate: () => void;
}

export function FundCallDetailModal({ fundCall, subsidyName, onClose, onUpdate }: FundCallDetailModalProps) {
  const [linkedInvoices, setLinkedInvoices] = useState<LinkedInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadLinkedInvoices();
  }, [fundCall.id]);

  const loadLinkedInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from('grant_fund_call_invoices')
        .select(`
          id,
          invoice_id,
          allocated_amount,
          justification_note,
          invoice:grant_invoices(
            supplier,
            invoice_date,
            total_amount,
            invoice_number
          )
        `)
        .eq('fund_call_id', fundCall.id)
        .order('linked_at', { ascending: false });

      if (error) throw error;
      setLinkedInvoices(data || []);
    } catch (error) {
      console.error('Error loading linked invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (linkId: string) => {
    if (!confirm('Voulez-vous vraiment retirer ce lien ?')) return;

    try {
      const { error } = await supabase
        .from('grant_fund_call_invoices')
        .delete()
        .eq('id', linkId);

      if (error) throw error;
      loadLinkedInvoices();
    } catch (error) {
      console.error('Error unlinking invoice:', error);
      alert('Erreur lors de la suppression du lien');
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const totalAllocated = linkedInvoices.reduce((sum, li) => sum + (li.allocated_amount || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{fundCall.name}</h2>
            <p className="text-sm text-gray-600 mt-1">{subsidyName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Fund call info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-gray-600">Date ADF</div>
                <div className="text-lg font-semibold text-gray-900 mt-1">
                  {formatDate(fundCall.call_date)}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Montant demandé</div>
                <div className="text-lg font-semibold text-emerald-600 mt-1">
                  {formatCurrency(fundCall.requested_amount)}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Montant versé</div>
                <div className="text-lg font-semibold text-green-600 mt-1">
                  {formatCurrency(fundCall.received_amount)}
                </div>
              </div>
            </div>
            {fundCall.usage_description && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Usage déclaré</div>
                <div className="text-sm text-gray-900">{fundCall.usage_description}</div>
              </div>
            )}
            {fundCall.notes && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Notes</div>
                <div className="text-sm text-gray-900">{fundCall.notes}</div>
              </div>
            )}
          </div>

          {/* Total allocated */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div>
              <div className="text-sm text-blue-800 font-medium">Total affecté aux factures</div>
              <div className="text-2xl font-bold text-blue-900 mt-1 flex items-center gap-2">
                <Euro className="w-6 h-6" />
                {formatCurrency(totalAllocated)}
              </div>
            </div>
            {fundCall.requested_amount && totalAllocated > fundCall.requested_amount && (
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm font-medium">Dépasse le montant demandé</span>
              </div>
            )}
          </div>

          {/* Linked invoices */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Factures liées</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Créer et lier
                </button>
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
                >
                  <LinkIcon className="w-4 h-4" />
                  Lier existante
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : linkedInvoices.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">Aucune facture liée à cet ADF</p>
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Créer une facture
                  </button>
                  <button
                    onClick={() => setShowLinkModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Lier une facture existante
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {linkedInvoices.map((link) => {
                  const invoice = link.invoice as any;
                  return (
                    <div
                      key={link.id}
                      className="p-4 bg-white border border-gray-200 rounded-lg hover:border-emerald-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <div>
                              <div className="font-medium text-gray-900">{invoice.supplier}</div>
                              <div className="text-sm text-gray-600">
                                {invoice.invoice_number && `N° ${invoice.invoice_number} • `}
                                {formatDate(invoice.invoice_date)}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-4 pl-8">
                            <div>
                              <div className="text-xs text-gray-600">Montant total facture</div>
                              <div className="text-sm font-medium text-gray-900">{formatCurrency(invoice.total_amount)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600">Part de cet ADF</div>
                              <div className="text-sm font-semibold text-emerald-600">{formatCurrency(link.allocated_amount)}</div>
                            </div>
                          </div>
                          {link.justification_note && (
                            <div className="mt-2 pl-8 text-sm text-gray-600">
                              <span className="font-medium">Note:</span> {link.justification_note}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleUnlink(link.id)}
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                          Retirer
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-emerald-600 border border-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
          >
            <Edit className="w-4 h-4" />
            Modifier l'ADF
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditFundCallModal
          fundCall={fundCall}
          subsidyName={subsidyName}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            onUpdate();
          }}
        />
      )}

      {showLinkModal && (
        <LinkInvoiceModal
          fundCallId={fundCall.id}
          fundCallName={fundCall.name}
          onClose={() => setShowLinkModal(false)}
          onSuccess={() => {
            setShowLinkModal(false);
            loadLinkedInvoices();
          }}
        />
      )}

      {showCreateModal && (
        <CreateInvoiceModal
          fundCallId={fundCall.id}
          fundCallName={fundCall.name}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadLinkedInvoices();
          }}
        />
      )}
    </div>
  );
}
